/**
 * usePredictionAccuracy Hook
 *
 * Fetches demand forecasts and calculates accuracy metrics per strain.
 * Used by the Prediction Model screen to show forecast performance.
 *
 * Part of US-FORECAST-CALIBRATION Step 7
 * Updated: Now uses unified demand_forecasts table (US-FORECAST-UNIFIED)
 *
 * @example
 * const {
 *   data,
 *   loading,
 *   error,
 *   refetch
 * } = usePredictionAccuracy({ strainId: 'uuid', weeks: 12 })
 */

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/supabase-client'
import { toLocalDateString } from '../utils/dateUtils'
import { calculateMAPE, calculateBias } from '@/services/forecastStatistics'

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get Monday of the week containing the given date
 * @param {string} dateStr - Date string (YYYY-MM-DD)
 * @returns {string} Monday date in YYYY-MM-DD format
 */
function getWeekStart(dateStr) {
  const d = new Date(dateStr)
  const day = d.getDay()
  const diff = day === 0 ? -6 : 1 - day
  d.setDate(d.getDate() + diff)
  return toLocalDateString(d)
}

/**
 * Group daily forecasts by strain and week
 * @param {Array} forecasts - Array of demand forecast records
 * @returns {Map} Map of strainId -> Map of weekStart -> aggregated data
 */
function groupByStrainAndWeek(forecasts) {
  const grouped = new Map()

  for (const forecast of forecasts) {
    const strainId = forecast.strain_id
    const weekStart = forecast.week_start || getWeekStart(forecast.forecast_date)

    if (!grouped.has(strainId)) {
      grouped.set(strainId, {
        strainName: forecast.strains?.name,
        weeks: new Map()
      })
    }

    const strainData = grouped.get(strainId)

    if (!strainData.weeks.has(weekStart)) {
      strainData.weeks.set(weekStart, {
        weekStart,
        rawTotalKg: 0,
        calibratedTotalKg: 0,
        actualTotalKg: 0,
        hasActuals: false,
        calibrationFactorSum: 0,
        dayCount: 0
      })
    }

    const weekData = strainData.weeks.get(weekStart)
    weekData.rawTotalKg += (forecast.recurring_kg || 0) + (forecast.predicted_kg || 0)
    weekData.calibratedTotalKg += forecast.calibrated_kg || 0
    weekData.actualTotalKg += forecast.actual_kg || 0
    weekData.calibrationFactorSum += forecast.calibration_factor || 1
    weekData.dayCount += 1

    if (forecast.is_actual) {
      weekData.hasActuals = true
    }
  }

  return grouped
}

/**
 * Calculate simple average of an array
 * @param {number[]} arr - Array of numbers
 * @returns {number} Average value, or 0 if empty
 */
function average(arr) {
  if (!arr || arr.length === 0) return 0
  const validValues = arr.filter(v => v != null && !isNaN(v))
  if (validValues.length === 0) return 0
  return validValues.reduce((a, b) => a + b, 0) / validValues.length
}

// =============================================================================
// Main Hook: usePredictionAccuracy
// =============================================================================

/**
 * Hook to fetch prediction accuracy metrics
 *
 * @param {Object} options - Hook options
 * @param {string} [options.strainId=null] - Filter to specific strain (null = all strains)
 * @param {number} [options.weeks=12] - Number of weeks of history to fetch
 * @param {boolean} [options.enabled=true] - Whether to fetch data
 * @returns {Object} { data, loading, error, refetch }
 */
export function usePredictionAccuracy({ strainId = null, weeks = 12, enabled = true } = {}) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchAccuracy = useCallback(async () => {
    if (!enabled) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Calculate date range (past X weeks)
      const endDate = new Date()
      endDate.setHours(0, 0, 0, 0)
      const startDate = new Date(endDate)
      startDate.setDate(startDate.getDate() - (weeks * 7))

      const startDateStr = toLocalDateString(startDate)
      const endDateStr = toLocalDateString(endDate)

      // Build query - fetch from demand_forecasts
      let query = supabase
        .from('demand_forecasts')
        .select(`
          id,
          strain_id,
          forecast_date,
          recurring_kg,
          predicted_kg,
          calibrated_kg,
          actual_kg,
          calibration_factor,
          is_actual,
          week_start,
          strains:strain_id (
            id,
            name
          )
        `)
        .eq('is_actual', true)  // Only past dates with actuals
        .gte('forecast_date', startDateStr)
        .lte('forecast_date', endDateStr)
        .order('forecast_date', { ascending: false })

      if (strainId) {
        query = query.eq('strain_id', strainId)
      }

      const { data: forecasts, error: fetchError } = await query

      if (fetchError) {
        throw fetchError
      }

      if (!forecasts || forecasts.length === 0) {
        setData([])
        return
      }

      // Group by strain and week
      const grouped = groupByStrainAndWeek(forecasts)

      // Calculate metrics per strain
      const metrics = []

      for (const [id, strainData] of grouped) {
        // Filter to weeks with actuals
        const weeksWithActuals = [...strainData.weeks.values()]
          .filter(w => w.hasActuals)

        if (weeksWithActuals.length === 0) continue

        const predictions = weeksWithActuals.map(w => w.calibratedTotalKg)
        const actuals = weeksWithActuals.map(w => w.actualTotalKg)
        const rawPredictions = weeksWithActuals.map(w => w.rawTotalKg)

        const mape = calculateMAPE(predictions, actuals)
        const bias = calculateBias(predictions, actuals)
        const rawMape = calculateMAPE(rawPredictions, actuals)

        // Calculate calibration improvement
        const calibrationImprovement = rawMape != null && mape != null
          ? rawMape - mape
          : null

        // Calculate average calibration factor
        const avgCalibrationFactor = average(
          weeksWithActuals.map(w => w.calibrationFactorSum / w.dayCount)
        )

        // Build weekly records for detail view
        const records = weeksWithActuals.map(w => ({
          prediction_week: w.weekStart,
          raw_predicted_kg: Math.round(w.rawTotalKg * 100) / 100,
          predicted_kg: Math.round(w.calibratedTotalKg * 100) / 100,
          actual_kg: Math.round(w.actualTotalKg * 100) / 100,
          error_kg: Math.round((w.calibratedTotalKg - w.actualTotalKg) * 100) / 100,
          error_percent: w.actualTotalKg > 0
            ? Math.round(((w.calibratedTotalKg - w.actualTotalKg) / w.actualTotalKg) * 10000) / 100
            : null
        })).sort((a, b) => b.prediction_week.localeCompare(a.prediction_week))

        metrics.push({
          strain_id: id,
          strain_name: strainData.strainName,
          mape,
          bias,
          raw_mape: rawMape,
          calibration_improvement: calibrationImprovement,
          sample_count: weeksWithActuals.length,
          avg_calibration_factor: avgCalibrationFactor,
          latest_week: weeksWithActuals[weeksWithActuals.length - 1]?.weekStart,
          records
        })
      }

      // Sort by sample count (most data first)
      metrics.sort((a, b) => b.sample_count - a.sample_count)

      setData(metrics)
    } catch (err) {
      console.error('[usePredictionAccuracy] Error:', err)
      setError(err.message || 'Failed to fetch prediction accuracy data')
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [strainId, weeks, enabled])

  useEffect(() => {
    fetchAccuracy()
  }, [fetchAccuracy])

  return {
    data,
    loading,
    error,
    refetch: fetchAccuracy
  }
}

// =============================================================================
// Helper Hook: useForecastSnapshots
// =============================================================================

/**
 * Hook to fetch weekly demand forecasts for a specific strain and week
 *
 * @param {Object} options - Hook options
 * @param {string} options.strainId - Strain UUID
 * @param {string} options.weekStart - Week start date (YYYY-MM-DD)
 * @param {boolean} [options.enabled=true] - Whether to fetch data
 * @returns {Object} { data, loading, error, refetch }
 */
export function useForecastSnapshots({ strainId, weekStart, enabled = true }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchSnapshots = useCallback(async () => {
    if (!enabled || !strainId || !weekStart) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data: forecasts, error: fetchError } = await supabase
        .from('demand_forecasts')
        .select(`
          id,
          strain_id,
          forecast_date,
          recurring_kg,
          predicted_kg,
          calibrated_kg,
          actual_kg,
          calibration_factor,
          is_actual,
          week_start
        `)
        .eq('strain_id', strainId)
        .eq('week_start', weekStart)
        .order('forecast_date')

      if (fetchError) {
        throw fetchError
      }

      // Aggregate daily data into weekly summary
      if (forecasts && forecasts.length > 0) {
        const weekly = {
          weekStart,
          strainId,
          recurring_kg: forecasts.reduce((sum, f) => sum + (f.recurring_kg || 0), 0),
          predicted_kg: forecasts.reduce((sum, f) => sum + (f.predicted_kg || 0), 0),
          calibrated_kg: forecasts.reduce((sum, f) => sum + (f.calibrated_kg || 0), 0),
          actual_kg: forecasts.reduce((sum, f) => sum + (f.actual_kg || 0), 0),
          calibration_factor: forecasts.reduce((sum, f) => sum + (f.calibration_factor || 1), 0) / forecasts.length,
          dailyBreakdown: forecasts
        }
        weekly.raw_predicted_kg = weekly.recurring_kg + weekly.predicted_kg
        setData(weekly)
      } else {
        setData(null)
      }
    } catch (err) {
      console.error('[useForecastSnapshots] Error:', err)
      setError(err.message || 'Failed to fetch forecast snapshots')
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [strainId, weekStart, enabled])

  useEffect(() => {
    fetchSnapshots()
  }, [fetchSnapshots])

  return {
    data,
    loading,
    error,
    refetch: fetchSnapshots
  }
}

// =============================================================================
// Legacy Alias (for backward compatibility)
// =============================================================================

/**
 * @deprecated Use useForecastSnapshots instead
 */
export const usePredictionSnapshots = useForecastSnapshots

// =============================================================================
// Default Export
// =============================================================================

export default usePredictionAccuracy
